(() => {
    'use strict';

    const updateWidget = (widget, mode = 'view', readyFn = null) => {
        if (widget.hasClass('removing')) {
            return;
        }

        const animDuration = howspace.getAnimationDuration(widget, '--widget-animation-duration');

        widget.addClass('removing');

        const pageMode = real.dom.$body.is('.edit-page') ? 'edit' : 'view';

        // The user can lose sight of a widget after the page was drawn. That refusal is expected, so it is not
        // reported, and the widget keeps its current content.
        const isWidgetNotViewable = response => real.ajax.hasErrorKey(response, 'widget_not_viewable');

        real.ajax.html(getUrl(widget) + '/' + mode + '?page_mode=' + pageMode, {
            type: 'GET',
            showErrorNotification: response => !isWidgetNotViewable(response),
            logError: response => !isWidgetNotViewable(response),
            error: response => {
                if (isWidgetNotViewable(response)) {
                    widget.removeClass('removing');
                }
            },
            success: response => {
                // Put the new widget to DOM, but hide it for now with d-none class.
                const updatedWidget = $(response);
                widget.after(updatedWidget[0]);
                updatedWidget.addClass('updating');

                // Add initialized class to text editors at this points so that they do not get initialized yet.
                updatedWidget.find('.howspace-text-editor').addClass('howspace-text-editor-initialized');

                // Init the new widget.
                initWidget(updatedWidget);

                // Calculate the height of the new widget.
                const animHeight = updatedWidget.outerHeight();
                updatedWidget.addClass('d-none');

                // Hide overflow for the duration of the animation.
                widget.css('overflow', 'hidden');
                updatedWidget.css('overflow', 'hidden');

                // Fade out all direct children.
                widget.children().velocity('fadeOut', animDuration);

                // Animate old widget height to match the new widget height.
                widget.velocity({
                    height: animHeight
                }, {
                    duration: animDuration,
                    complete: () => {
                        // Destroy the old widget.
                        const checkScroll = widget.data('check-scroll-into-view');
                        // Removing the widget drops focus from its menu button, so the new widget's button gets it.
                        const hadMenuFocus = widget[0].contains(document.activeElement)
                            && document.activeElement.matches('[data-toggle="dropdown"]');
                        destroyWidget(widget);
                        widget.remove();

                        // Show the new widget and temporarily force the height to the animation height.
                        updatedWidget.removeClass('d-none').height(animHeight);

                        // Check do we need to scroll the widget into view after the update.
                        if (checkScroll === true) {
                            howspace.scroller.scrollToElement(updatedWidget, 0.1);
                        }

                        requestAnimationFrame(() => {
                            // The children fade in animation has started. Clear the forced height and overflow.
                            updatedWidget.css({'height': '', 'overflow': '', 'display': ''});
                        });

                        // Init text editors now.
                        updatedWidget.find('.howspace-text-editor').removeClass('howspace-text-editor-initialized');
                        howspace.components.textEditor.init(updatedWidget);

                        // Fade in the direct children.
                        const children = updatedWidget.children();
                        let count = children.length;
                        children.each(function () {
                            const child = $(this);
                            if (child.hasClass('d-none')) {
                                // Skip hidden children
                                return true;
                            }
                            child.hide();
                            const options = {
                                duration: animDuration,
                                complete: function () {
                                    count--;
                                    child.css({'display': '', 'opacity': ''});
                                    if (count === 0) {
                                        // All children have been faded in, widget is ready.
                                        updatedWidget.removeClass('updating');
                                        if (hadMenuFocus && document.activeElement === document.body) {
                                            updatedWidget.find('[data-toggle="dropdown"]').first().trigger('focus');
                                        }
                                        if (typeof readyFn === 'function') {
                                            readyFn(updatedWidget);
                                        }
                                    }
                                }
                            };

                            if (child.is('.grid')) {
                                options.display = 'grid';
                            }
                            if (child.is('.flex')) {
                                options.display = 'flex';
                            }

                            // Don't use the fadeIn, because in edit page the target opacity might be less than 1.
                            const animProps = {opacity: parseFloat(child.css('opacity'))};
                            child.css({opacity: 0, display: ''});
                            child.velocity(animProps, options);
                        });
                    }
                });
            }
        });
    };

    const initWidget = async widget => {
        if (!(widget instanceof jQuery) || widget.length === 0) {
            return null;
        }

        // Add initialized class (so that the widget is not accidentally initialized more than once).
        widget.addClass('widget-initialized');

        // Init components.
        howspace.components.init(widget);

        // Init the widget.
        const type = getType(widget);
        const mode = widget.data('widget-mode');

        let genericInitFn = null;
        if (mode === 'view') {
            genericInitFn = initViewWidget;
        } else if (mode === 'edit') {
            genericInitFn = initEditWidget;
        } else {
            // Unknown mode.
            console.error('Unknown widget mode: ' + mode);
            return null;
        }

        let initPromise = null;
        const initFnName = 'init' + mode.charAt(0).toUpperCase() + mode.slice(1);
        if (howspace.widgets[type] && typeof howspace.widgets[type][initFnName] === 'function') {
            initPromise = howspace.widgets[type][initFnName](widget);
        }

        genericInitFn(widget);

        return initPromise;
    };

    const initViewWidget = widget => {
        // Handle "full screen" buttons for all widgets.
        widget.find('.full-screen-btn').on('click', () => {
            howspace.fullscreen.toggle(widget);
        });

        // Handle the "edit widget" button for all widgets.
        const editBtn = widget.find('.edit-widget-btn');
        editBtn.on('click', () => {
            if (editBtn.is('.disabled, [disabled]')) {
                return false;
            }
            real.button.loading(editBtn);
            howspace.components.dropdown.close(editBtn.closest('.dropdown'));
            const focusFirstInput = howspace.focusFirstInput;
            updateWidget(widget, 'edit', (updatedWidget) => {
                real.button.reset(editBtn);
                if (focusFirstInput) {
                    howspace.form.focusFirstInput(updatedWidget);
                }
            });
        });

        // Handle "double click edit" for all widgets.
        widget.find('.widget-title').on('dblclick', () => {
            editBtn.trigger('click');
        });

        // Handle the "export to excel" button for all widgets.
        widget.find('.export-widget-btn:not(.disabled)').on('click', howspace.widgets.startExport);
        // An export rendered as running frees the button on its own if the export stops reporting.
        const runningExportBtn = widget.find('.export-widget-btn[data-export-progress]');
        if (runningExportBtn.length > 0) {
            setExportProgress(getId(widget), runningExportBtn.attr('data-export-progress'), exportQueuedTimeout);
        }

        // Handle "change style" for all widgets.
        widget.find('.change-widget-style-btn').on('click', e => {
            const btn = $(e.currentTarget);
            const dropdown = btn.closest('.dropdown');

            real.button.loading(btn);

            const dataPayload = {
                style: btn.data('widget-style')
            };

            real.ajax.json(getApiUrl(widget), {
                type: 'POST',
                data: JSON.stringify(dataPayload),
                complete: () => {
                    real.button.reset(btn, dropdown);
                    btn.prop('disabled', true);
                    howspace.components.dropdown.close(dropdown);
                }
            });

            e.stopPropagation();
            return false;
        });

        widget.on('click', '.wordcloud-widget-btn, .summary-widget-btn', function () {
            const btn = $(this);
            const widget = btn.closest('.widget');
            const viewType = btn.data('ai-view-type');
            howspace.components.modal.show(
                real.baseUri + '/ai/modal/full-screen',
                (modal) => {
                    modal = $(modal);
                    modal.on('click', '.ai-save-as-widget', (e) => {
                        const btn = $(e.target);
                        real.button.loading(btn, modal);

                        const dataPayload = {
                            widget: {
                                type: viewType,
                                params: {
                                    source_widget_type: howspace.widgets.getType(widget),
                                    source_widget: howspace.widgets.getId(widget),
                                    state: 'ready',
                                }
                            }
                        };

                        const translationParam = {original_widget_title: modal.find('.modal-title').text().trim()};
                        if (viewType === 'wordcloud') {
                            dataPayload.widget.params.title = howspace.translate('wordcloud_referenced_title', translationParam);
                            dataPayload.widget.params.items = modal.find('.howspace-wordcloud').data('items');
                        } else if (viewType === 'summary') {
                            dataPayload.widget.params.title = howspace.translate('summary_referenced_title', translationParam);
                            dataPayload.widget.params.text = modal.find('.howspace-summary').data('text');
                        }

                        const nextWidget = widget.nextAll('.widget').first();
                        if (nextWidget.length > 0) {
                            dataPayload.before_widget = {
                                id: nextWidget.data('widget-id'),
                                type: nextWidget.data('widget-type')
                            };
                        }

                        const pageId = modal.data('page-id');
                        const columnId = modal.data('column-id');
                        real.ajax.json(`${real.baseUri}/api/page/${pageId}/column/${columnId}/add-widget`, {
                            type: 'POST',
                            data: JSON.stringify(dataPayload),
                            success: () => {
                                // Adds a slight delay in the close, but needed if we want to show backend errors in the modal
                                howspace.components.modal.close(modal);
                            },
                            error: () => {
                                real.button.reset(btn, modal);
                            }
                        });

                    });
                },
                {
                    fullScreen: true,
                    postDataFn: () => {
                        return {
                            view_type: viewType,
                            source_widget_type: howspace.widgets.getType(widget),
                            source_widget_id: howspace.widgets.getId(widget),
                            target_lang: howspace.components.translator.getTargetLang()
                        };
                    }
                }
            );
        });
    };

    const initEditWidget = widget => {
        // Handle the "cancel widget" button for all widgets.
        const cancelBtn = widget.find('.cancel-widget-btn');
        cancelBtn.on('click', () => {
            real.button.loading(cancelBtn, widget);
            widget.data('check-scroll-into-view', true);
            updateWidget(widget, 'view');
        });

        // Handle the "save widget" button for all widgets.
        const saveBtn = widget.find('.save-widget-btn');
        saveBtn.on('click', () => {
            real.button.loading(saveBtn, widget);

            howspace.components.textEditor.waitForTinyMCE().then(() => {
                let dataPayload = real.form.getDataPayload(widget);

                const type = getType(widget);
                const payloadFn = 'save';
                if (howspace.widgets[type] && typeof howspace.widgets[type][payloadFn] === 'function') {
                    dataPayload = howspace.widgets[type][payloadFn](widget, dataPayload);
                    if (!dataPayload) {
                        real.button.reset(saveBtn, widget);
                        return;
                    }
                }

                if (isParallel(widget)) {
                    dataPayload = {
                        settings: dataPayload
                    };
                }

                let errorCallbackFn = 'error';
                let errorFn = (response, widget, dataPayload) => {
                    real.button.reset(saveBtn, widget);
                    widget.removeData('check-scroll-into-view');
                };

                // Check if the widget has a custom error callback function.
                // If it does, use that instead of the default one.
                if (howspace.widgets[type] && typeof howspace.widgets[type][errorCallbackFn] === 'function') {
                    errorFn = howspace.widgets[type][errorCallbackFn];
                }

                widget.data('check-scroll-into-view', true);

                real.ajax.json(getApiUrl(widget), {
                    type: 'POST',
                    data: JSON.stringify(dataPayload),
                    error: response => {
                        errorFn(response, widget, dataPayload);
                    }
                });
            });
        });
    };

    const destroyWidget = widget => {
        // Remove the initialized class.
        widget.removeClass('widget-initialized');

        // Destroy components.
        howspace.components.destroy(widget);

        // Destroy the widget.
        const type = getType(widget);
        const mode = widget.data('widget-mode') || 'view';
        const destroyFn = 'destroy' + mode.charAt(0).toUpperCase() + mode.slice(1);
        if (howspace.widgets[type] && typeof howspace.widgets[type][destroyFn] === 'function') {
            howspace.widgets[type][destroyFn](widget);
        }

        // Remove all event handlers and jQuery data.
        widget.find('*').off().removeData();
        // Remove all event handlers and jQuery data from the widget itself if any delegate event is bound to it.
        widget.off().removeData();
    };

    real.events.on('widget:updated', notification => {
        const widget = $('#widget-' + notification.object.widget.id);
        const isSpacerWidget = widget.hasClass('widget-spacer');

        // Do not update the spacer widget if the current user is chaning height in edit page.
        if (isSpacerWidget && real.events.isFromCurrentUser(notification) && real.dom.$body.is('.edit-page') && widget.data('widget-mode') === 'view') {
            return;
        }

        if (widget.length > 0) {
            updateWidget(widget, 'view');
        }
        getWidgets(notification).each(function () {
            const widget = $(this);
            if (widget.prop('id') !== 'widget-' + notification.object.widget.id) {
                updateWidget($(this), 'view');
            }
        });
    });

    real.events.on('widget:export:completed', notification => {
        const params = {
            'href': `<a class="real-link" data-link-type="download" href="${notification.object.uploadedfile.href}">${notification.object.uploadedfile.filename}</a>`
        };
        let message = howspace.translate('widget_export_completed_with_link', params);
        if (notification.object.skipped_attachments > 0) {
            message += ' ' + howspace.translate('widget_export_attachments_left_out', {
                count: notification.object.skipped_attachments,
                limit: notification.object.attachment_size_limit
            });
        }
        // The notification bar lays its children out as flex items, so one span keeps the sentences flowing as text.
        real.notification.show(`<span>${message}</span>`, 'success');
    });

    real.events.on('widget:export:failed', () => {
        real.notification.show(howspace.translate('widget_export_failed_on_our_side'), 'danger');
    });

    // Sent only to the user who started the export, so every tab of theirs shows it and nobody else's does.
    real.events.on('widget:export:progress', notification => {
        if (notification.object.is_done) {
            resetExportButtons(notification.object.widget.id);
        } else {
            setExportProgress(notification.object.widget.id, notification.object.progress);
        }
    });

    // A running export refreshes its lock on the server well within a minute, so a silence that long means it died.
    // Before its worker starts, the export can wait in the queue for up to ten minutes.
    const exportSilenceTimeout = 60000;
    const exportQueuedTimeout = 600000;
    const exportSilenceTimers = {};

    const getExportButtons = widgetId => {
        return $(
            `.widget[data-widget-id="${widgetId}"] .export-widget-btn, ` +
            `.widget[data-source-widget-id="${widgetId}"] .export-widget-btn`
        );
    };

    const setExportProgress = (widgetId, progress, timeout = exportSilenceTimeout) => {
        const text = howspace.translate('widget_export_in_progress', {percentage: progress + '%'});
        getExportButtons(widgetId).each(function () {
            const btn = $(this);
            btn.prop('disabled', true).attr('aria-label', text).attr('data-export-progress', progress);
            btn.find('.btn-text').text(text);
        });

        clearTimeout(exportSilenceTimers[widgetId]);
        exportSilenceTimers[widgetId] = setTimeout(() => resetExportButtons(widgetId), timeout);
    };

    const resetExportButtons = widgetId => {
        clearTimeout(exportSilenceTimers[widgetId]);
        delete exportSilenceTimers[widgetId];

        getExportButtons(widgetId).each(function () {
            const btn = $(this);
            const text = btn.attr('data-export-text');
            btn.prop('disabled', false).attr('aria-label', text).removeAttr('data-export-progress');
            btn.find('.btn-text').text(text);
        });
    };

    const isParallel = widget => {
        widget = $(widget);
        return widget.hasClass('widget-parallel') && widget.data('source-widget-type') && widget.data('source-widget-id');
    };

    const hasParallel = widget => {
        return $(`.widget.widget-parallel[data-source-widget-id="${widget.data('widget-id')}"][data-source-widget-type="${widget.data('widget-type')}"]`).length > 0;
    };

    const getParallel = widget => {
        return $(`.widget.widget-parallel[data-source-widget-id="${widget.data('widget-id')}"][data-source-widget-type="${widget.data('widget-type')}"]`);
    };

    const getUrl = (widget) => {
        if (widget instanceof jQuery) {
            widget = widget[0];
        }
        return real.baseUri + '/widget/' + widget.dataset.widgetType + '/' + widget.dataset.widgetId;
    };

    const getApiUrl = (widget) => {
        if (widget instanceof jQuery) {
            widget = widget[0];
        }
        return real.baseUri + '/api/widget/' + widget.dataset.widgetType + '/' + widget.dataset.widgetId;
    };

    const getSourceApiUrl = (widget) => {
        if (widget instanceof jQuery) {
            widget = widget[0];
        }
        return real.baseUri + '/api/widget/' + getType(widget) + '/' + getId(widget);
    };

    const getId = widget => {
        if (widget instanceof jQuery) {
            widget = widget[0];
        }
        if (isParallel(widget)) {
            return widget.dataset.sourceWidgetId;
        }
        return widget.dataset.widgetId;
    }

    const getType = widget => {
        if (widget instanceof jQuery) {
            widget = widget[0];
        }
        if (isParallel(widget)) {
            return widget.dataset.sourceWidgetType;
        }
        return widget.dataset.widgetType;
    };

    const startExport = e => {
        const btn = $(e.currentTarget);
        const widget = btn.closest('.widget');
        const widgetId = getId(widget);

        real.button.loading(btn);

        let dataPayload = {
            _action: 'export_excel'
        };

        const type = getType(widget);
        const payloadFn = 'export';
        if (howspace.widgets[type] && typeof howspace.widgets[type][payloadFn] === 'function') {
            dataPayload = howspace.widgets[type][payloadFn](widget, dataPayload);
        }

        real.ajax.json(getApiUrl(widget), {
            type: 'POST',
            data: JSON.stringify(dataPayload),
            // A second export of the same widget is refused with a message for the user, not an error to log.
            logError: response => response.status !== 409,
            success: () => {
                real.notification.show(howspace.translate('widget_export_started'), 'light');

                real.button.reset(btn);
                setExportProgress(widgetId, 0, exportQueuedTimeout);
            },
            error: response => {
                real.button.reset(btn);
                // Refused because this user's export of the widget is already running, which the button shows again.
                if (response.status === 409) {
                    setExportProgress(widgetId, btn.attr('data-export-progress') ?? 0, exportQueuedTimeout);
                }
            }
        });
    };

    /**
     * Attaches an event listener to server-side websocket events. Must be called outside widget initView/initEdit
     * functions to prevent multiple event listeners being attached on every widget update.
     */
    const onEvent = (action, fn) => {
        real.events.on(action, notification => {
            getWidgets(notification).each(function () {
                fn($(this), notification);
            });
        });
    };

    const getWidgets = (notification) => {
        return $(`.widget[data-widget-id="${notification.object.widget.id}"]`);
    };

    const getFilter = (widget) => {
        let filter = {};

        if (widget.data('limit-results-to-user-profile-field')) {
            let userProfileFields = [];
            widget.data('limit-results-to-user-profile-field').split(',').forEach(function (item) {
                const userProfileField = [item.substring(0, item.indexOf('_')), item.substring(item.indexOf('_') + 1)];
                userProfileFields.push({
                    user_profile_field_id: userProfileField[0],
                    value: userProfileField[1]
                });
            });
            filter['limit_results_to_user_profile_field'] = userProfileFields;
        }

        if (widget.data('limit-results-to-team')) {
            filter['limit_results_to_team'] = widget.data('limit-results-to-team');
        }

        if (widget.data('limit-results-to-user')) {
            filter['limit_results_to_user'] = widget.data('limit-results-to-user');
        }

        if (widget.data('limit-results-to-userlist')) {
            filter['limit_results_to_userlist'] = widget.data('limit-results-to-userlist');
        }

        if (widget.data('limit-results-to-annotation')) {
            filter['limit_results_to_annotation'] = widget.data('limit-results-to-annotation');
        }

        if (widget.data('limit-results-to-whats-new')) {
            filter['limit_results_to_whats_new'] = true;
        }

        if (widget.data('limit-results-to-comment-id')) {
            filter['limit_results_to_comment_id'] = widget.data('limit-results-to-comment-id');
        }

        if (Object.keys(filter).length === 0) {
            return null;
        }

        return filter;
    };

    const triggerFilterChanged = (widget) => {
        const type = getType(widget);
        const updateFn = 'updateFilter';
        if (howspace.widgets[type] && typeof howspace.widgets[type][updateFn] === 'function') {
            howspace.widgets[type][updateFn](widget);
        }
    };

    const clearFilters = (widget) => {
        widget.removeDataAndAttr('limit-results-to-annotation');
        widget.removeDataAndAttr('limit-results-to-team');
        widget.removeDataAndAttr('limit-results-to-user');
        widget.removeDataAndAttr('limit-results-to-user-profile-field');
        widget.removeDataAndAttr('limit-results-to-userlist');
        widget.removeDataAndAttr('limit-results-to-whats-new');
        widget.removeDataAndAttr('limit-results-to-comment-id');
    };

    const setFilter = (widget, filter) => {
        // remove existing data attributes
        clearFilters(widget);

        // loop filter and set data attributes
        for (const [key, value] of Object.entries(filter)) {
            widget.dataAndAttr('limit-results-to-' + key, value);
        }

        triggerFilterChanged(widget);
    };

    const saveFilter = (widget, filter = getFilter(widget)) => {
        let dataPayload = {
            filter: filter
        };

        const type = getType(widget);
        const saveFn = 'saveFilter';
        if (howspace.widgets[type] && typeof howspace.widgets[type][saveFn] === 'function') {
            dataPayload = howspace.widgets[type][saveFn](widget, dataPayload);
        }

        real.ajax.json(getApiUrl(widget), {
            type: 'POST',
            data: JSON.stringify(dataPayload),
        });
    };

    const addFilter = (widget, filterType, filterValue) => {
        widget.dataAndAttr('limit-results-to-' + filterType, filterValue);

        triggerFilterChanged(widget);
    }

    const removeFilter = (widget, filterType, filterValue) => {
        const dataName = 'limit-results-to-' + filterType;
        const dataValue = widget.data(dataName);

        if (dataValue) {
            const values = dataValue.toString().split(',');
            const index = values.indexOf(filterValue);

            if (index > -1) {
                values.splice(index, 1);

                if (values.length === 0) {
                    widget.removeDataAndAttr(dataName);
                } else {
                    widget.dataAndAttr(dataName, values.join(','));
                }

                triggerFilterChanged(widget);
            }
        }
    };

    const getLocalStorageKey = (widget, key) => {
        return '#' + widget.prop('id') + '_' + real.dom.$body.data('pseudo-id') + '_' + key;
    };

    const removeLocalStorage = (widget, key) => {
        real.localStorage.remove(getLocalStorageKey(widget, key));
    };

    const getLocalStorage = (widget, key) => {
        return real.localStorage.get(getLocalStorageKey(widget, key));
    };

    const setLocalStorage = (widget, key, value) => {
        real.localStorage.set(getLocalStorageKey(widget, key), value);
    };

    const initAddWidgetModal = (modal, modalOpener) => {
        modal = $(modal);
        modalOpener = $(modalOpener);

        const widgetItems = modal.find('.widget-group .widget-item, .quick-access-widgets > *');

        modal.on(howspace.components.searchInput.SEARCH, e => {
            const q = (e.detail.value || '').toLowerCase().trim();

            if (!q) {
                widgetItems.show();
                modal.find('.widget-group-container').show();
                modal.find('.no-results').addClass('d-none');
                return;
            }

            widgetItems.each((_, item) => {
                const widget = $(item);
                const text = widget.text().toLowerCase();
                let keywords = [];
                if (widget.data('widget-keywords')) {
                    keywords = widget.data('widget-keywords');
                } else if (widget.find('.add-widget-btn').data('widget-keywords')) {
                    keywords = widget.find('.add-widget-btn').data('widget-keywords');
                }

                const matches = text.includes(q) || keywords.some(keyword => keyword.toLowerCase().includes(q));

                widget.toggle(matches);
                widget.toggleClass('matches-search', matches);
            });

            modal.find('.widget-group-container').each((_, item) => {
                const group = $(item);

                const anyMatch =
                    group.find('.widget-item.matches-search').length > 0 ||
                    group.find('.quick-access-widgets > *.matches-search').length > 0;

                group.toggle(anyMatch);
            });

            const hasAnyMatch = widgetItems.filter('.matches-search').length > 0;
            modal.find('.no-results').toggleClass('d-none', hasAnyMatch);
        });

        modal.find('.add-widget-btn').on('click', e => {
            const btn = $(e.currentTarget);

            if (modalOpener.length === 0) {
                return;
            }
            const columnId = modalOpener.closest('.column').data('column-id');
            const pageId = real.dom.$body.data('page-id');
            const dataPayload = {
                widget: {
                    type: btn.data('widget-type'),
                    params: btn.data('widget-params')
                },
                open_in_edit: true
            };

            const nextWidget = modalOpener.closest('.widget-space').next('.widget');
            if (nextWidget.length > 0) {
                dataPayload.before_widget = {
                    id: nextWidget.data('widget-id'),
                    type: nextWidget.data('widget-type'),
                };
            }

            real.ajax.json(`${real.baseUri}/api/page/${pageId}/column/${columnId}/add-widget`, {
                type: 'POST',
                data: JSON.stringify(dataPayload),
                success: () => {
                    // Adds a slight delay in the close, but needed if we want to show backend errors in the modal
                    real.button.reset(btn);
                    howspace.components.modal.close(modal);
                },
                error: () => {
                    real.button.reset(btn);
                }
            });
        });
    };

    const find = (widgetId, widgetType) => {
        return $(`.widget[data-widget-id="${widgetId}"][data-widget-type="${widgetType}"]`);
    }

    howspace.widgets = {
        initWidgets: async elem => {
            let initPromisesCount = 0;
            howspace.workspace.page.setReadyToScroll(false);
            elem.find('.widget[data-widget-type][data-widget-mode]:not(.widget-initialized)').each(function () {
                const initPromise = initWidget($(this));
                if (initPromise instanceof Promise) {
                    initPromisesCount++;
                    initPromise.then(() => {
                        initPromisesCount--;
                        if (initPromisesCount <= 0) {
                            howspace.workspace.page.setReadyToScroll(true);
                        }
                    });
                }
            });
            if (initPromisesCount === 0) {
                howspace.workspace.page.setReadyToScroll(true);
            }
        },
        initWidget: initWidget,
        destroyWidgets: elem => {
            elem.find('.widget.widget-initialized').each(function () {
                destroyWidget($(this));
            });
        },
        destroyWidget: destroyWidget,
        updateWidget: updateWidget,
        getUrl: getUrl,
        getApiUrl: getApiUrl,
        getSourceApiUrl: getSourceApiUrl,
        startExport: startExport,
        onEvent: onEvent,
        getFilter: getFilter,
        setFilter: setFilter,
        saveFilter: saveFilter,
        addFilter: addFilter,
        removeFilter: removeFilter,
        clearFilters: clearFilters,
        removeLocalStorage: removeLocalStorage,
        getLocalStorage: getLocalStorage,
        setLocalStorage: setLocalStorage,
        getId: getId,
        getType: getType,
        isParallel: isParallel,
        hasParallel: hasParallel,
        getParallel: getParallel,
        initAddWidgetModal: initAddWidgetModal,
        find: find,
    };
})();
